
import Component from "vue-class-component"
import {Prop} from "vue-property-decorator"
import ApiHtmlTable from "@/report/components/ApiHtmlTable.vue"
import ApiChart, {TrendHighlight} from "@/report/components/ApiChart.vue"
import _ from "lodash"
import AbstractPage, {Chart, Table} from "@/report/pages/AbstractPage.vue"

@Component({components: {ApiHtmlTable, ApiChart}})
export default class Page extends AbstractPage {
  @Prop({default: null})
  numberToDisplay: number | null

  @Prop({default: false})
  hideNumber: boolean

  @Prop({default: "white"})
  bgColor: string

  @Prop({default: "var(--pcr-hdr-1-color)"})
  headingColor: string

  @Prop({default: false})
  hideDefaultBgImage: boolean

  @Prop({default: null})
  bgImage: string | null

  @Prop({default: "center /cover"})
  bgImagePlacement: string

  @Prop({default: null})
  heading: string | null

  @Prop({default: null})
  tables: Array<Table | string> | null

  @Prop({default: null})
  charts: Array<Chart> | null

  @Prop({default: false})
  darkMode: boolean

  @Prop({default: null})
  trendColors: Array<string> | null

  get bgImageFile(): string | null {
    return this.bgImage == null
      ? this.hideDefaultBgImage
        ? null // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        : `${this.name.slice(0, this.name.length - 4)}.jpg`
      : this.bgImage
  }

  get backgroundImageStyle(): {background: string} | null {
    return this.bgImageFile == null
      ? {background: `${this.bgColor}`}
      : {
          // eslint-disable-next-line sonarjs/no-nested-template-literals
          background: `${this.bgColor} url(${<string>require(`@/img/${this.bgImageFile}`)}) ${
            this.bgImagePlacement
          } no-repeat`,
        }
  }

  get pageNumber(): number {
    return this.numberToDisplay === null ? this.number : this.numberToDisplay
  }

  private getChartId(name: string): string | null {
    const charts = this.lastSnapshot.charts
    return name in charts ? charts[name] : name
  }

  private getLastQuarterlyHistoryValue(name: string): string | null {
    const qtrHistory = this.lastSnapshot.history.filter(
      h => new Date(h.at).getMonth() in [2, 5, 8, 11]
    )
    return qtrHistory.length > 0 ? _.last(qtrHistory)![name] : null
  }

  private getTrendHighlight(chart: Chart): TrendHighlight | null {
    const value = this.getLastQuarterlyHistoryValue(chart.name)
    return this.trendColors && value != null
      ? {
          value,
          color: chart.color!,
        }
      : null
  }

  get htmlTables(): Array<Table> | null {
    return this.tables === null
      ? null
      : this.tables.map(t => (typeof t === "string" ? this.getTable(t) : t))
  }

  get tableResources(): Array<Table> {
    const resources = this.getReportPage()
      .pageResources.filter(pr => pr.type === "Table")
      .map(pr => this.getTable(pr.name, pr.heading))
    return this.htmlTables == null ? resources : [...resources, ...this.htmlTables]
  }

  get chartResources(): Array<Chart> {
    const resources = this.getReportPage()
      .pageResources.filter(pr => pr.type === "Chart")
      .map((pr, index) =>
        this.trendColors === null
          ? this.getChart(pr.name, pr.heading)
          : {
              name: pr.name,
              title: pr.heading,
              trendHighlight: true,
              color: this.trendColors[index],
            }
      )
    return this.charts == null ? resources : [...resources, ...this.charts]
  }
}
